import React, { useEffect, useState } from "react";
import { CheckCircle2, Loader2, Paperclip, X } from "lucide-react";
import { Toaster, toast } from "sonner";
import { postApplication } from "../lib/api";
import { useT } from "../lib/i18n";

const ALLOWED_TYPES = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const MAX_FILE_BYTES = 5 * 1024 * 1024;
const KB = 1024;

export default function Careers() {
    const { t, lang } = useT();
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        message: "",
    });
    const [cv, setCv] = useState(null);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        document.title = lang === "it"
            ? "Carriere, Casati Consulting"
            : "Careers, Casati Consulting";
    }, [lang]);

    function update(k, v) {
        setForm((f) => ({ ...f, [k]: v }));
    }

    function pickCv(file) {
        if (!file) return setCv(null);
        if (file.size > MAX_FILE_BYTES) {
            toast.error(t.careers.tooLarge);
            return;
        }
        const ok = ALLOWED_TYPES.includes(file.type) ||
            /\.(pdf|doc|docx)$/i.test(file.name);
        if (!ok) {
            toast.error(t.careers.wrongType);
            return;
        }
        setCv(file);
    }

    async function onSubmit(e) {
        e.preventDefault();
        if (busy) return;
        if (form.name.trim().length < 2 || !form.email.includes("@")) {
            toast.error(t.careers.invalid);
            return;
        }
        if (form.phone.trim().length < 5) {
            toast.error(t.careers.phoneRequired);
            return;
        }
        if (!cv) {
            toast.error(t.careers.cvRequired);
            return;
        }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("name", form.name.trim());
            fd.append("email", form.email.trim());
            fd.append("phone", form.phone.trim());
            fd.append("message", form.message.trim());
            fd.append("language", lang);
            fd.append("cv", cv);
            await postApplication(fd);
            setDone(true);
            toast.success(t.careers.success);
        } catch {
            toast.error(t.careers.error);
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="bg-[#F9F9F7] text-[#1C1C1C]">
            <Toaster position="bottom-center" richColors closeButton />
            <section className="pt-20 md:pt-28 pb-8">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
                    <p className="eyebrow">{t.careers.eyebrow}</p>
                    <h1
                        className="font-display mt-6 text-5xl sm:text-7xl lg:text-[100px] tracking-tight font-medium leading-[0.92]"
                        style={{ letterSpacing: "-0.035em" }}
                        data-testid="careers-title"
                    >
                        {t.careers.titleA}
                        <br />
                        <span className="italic font-light">{t.careers.titleB}</span>
                    </h1>
                </div>
            </section>

            <section className="py-16 md:py-24 border-t hair">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-12 gap-y-12 md:gap-x-12">
                    <aside className="col-span-12 md:col-span-4 space-y-10">
                        <div>
                            <p className="eyebrow">{t.careers.aboutEyebrow}</p>
                            <p className="mt-4 leading-relaxed text-[#1C1C1C]/80">
                                {t.careers.aboutBody}
                            </p>
                        </div>
                        <div>
                            <p className="eyebrow">{t.careers.howEyebrow}</p>
                            <p className="mt-4 leading-relaxed text-[#1C1C1C]/80">
                                {t.careers.howBody}
                            </p>
                        </div>
                        <div>
                            <p className="eyebrow">{t.common.languages}</p>
                            <p className="mt-4">{t.common.languagesValue}</p>
                        </div>
                    </aside>

                    <div className="col-span-12 md:col-span-8">
                        {done ? (
                            <div
                                className="border hair p-10 md:p-14 bg-white flex flex-col items-start gap-5"
                                data-testid="careers-success"
                            >
                                <CheckCircle2 size={36} strokeWidth={1.5} className="text-[#9B7A58]" />
                                <h2
                                    className="font-display text-3xl md:text-4xl tracking-tight font-medium"
                                    style={{ letterSpacing: "-0.02em" }}
                                >
                                    {t.careers.successTitle}
                                </h2>
                                <p className="text-[#1C1C1C]/75 leading-relaxed max-w-md">
                                    {t.careers.successBody}
                                </p>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setDone(false);
                                        setForm({ name: "", email: "", phone: "", message: "" });
                                        setCv(null);
                                    }}
                                    className="mt-4 text-[12px] uppercase tracking-[0.22em] font-bold link-underline"
                                    data-testid="careers-success-reset"
                                >
                                    {t.careers.another}
                                </button>
                            </div>
                        ) : (
                            <form
                                onSubmit={onSubmit}
                                className="space-y-10"
                                data-testid="careers-form"
                                noValidate
                                encType="multipart/form-data"
                            >
                                <Field label={t.careers.f.name} required>
                                    <input
                                        type="text"
                                        value={form.name}
                                        onChange={(e) => update("name", e.target.value)}
                                        className="cf-input"
                                        autoComplete="name"
                                        required
                                        minLength={2}
                                        data-testid="careers-name"
                                    />
                                </Field>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                                    <Field label={t.careers.f.email} required>
                                        <input
                                            type="email"
                                            value={form.email}
                                            onChange={(e) => update("email", e.target.value)}
                                            className="cf-input"
                                            autoComplete="email"
                                            required
                                            data-testid="careers-email"
                                        />
                                    </Field>
                                    <Field label={t.careers.f.phone} required>
                                        <input
                                            type="tel"
                                            value={form.phone}
                                            onChange={(e) => update("phone", e.target.value)}
                                            className="cf-input"
                                            autoComplete="tel"
                                            required
                                            data-testid="careers-phone"
                                        />
                                    </Field>
                                </div>
                                <Field label={t.careers.f.message}>
                                    <textarea
                                        rows={5}
                                        value={form.message}
                                        onChange={(e) => update("message", e.target.value)}
                                        className="cf-input resize-none"
                                        placeholder={t.careers.f.messagePlaceholder}
                                        data-testid="careers-message"
                                    />
                                </Field>

                                <Field label={t.careers.f.cv} required>
                                    {cv ? (
                                        <div
                                            className="mt-2 flex items-center justify-between gap-4 border hair px-4 py-3 bg-white"
                                            data-testid="careers-cv-selected"
                                        >
                                            <span className="flex items-center gap-3 text-sm truncate">
                                                <Paperclip size={14} strokeWidth={1.5} className="text-[#9B7A58]" />
                                                <span className="truncate">{cv.name}</span>
                                                <span className="text-[#1C1C1C]/45 text-xs">
                                                    {(cv.size / KB).toFixed(0)} KB
                                                </span>
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => setCv(null)}
                                                className="text-[#1C1C1C]/60 hover:text-[#1C1C1C]"
                                                data-testid="careers-cv-remove"
                                                aria-label={t.careers.removeCv}
                                            >
                                                <X size={16} strokeWidth={1.5} />
                                            </button>
                                        </div>
                                    ) : (
                                        <label
                                            className="mt-2 flex items-center gap-3 border hair border-dashed px-4 py-4 cursor-pointer hover:border-[#9B7A58] transition-colors"
                                            data-testid="careers-cv-dropzone"
                                        >
                                            <Paperclip size={16} strokeWidth={1.5} className="text-[#9B7A58]" />
                                            <span className="text-sm text-[#1C1C1C]/70">
                                                {t.careers.f.cvPlaceholder}
                                            </span>
                                            <input
                                                type="file"
                                                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                                onChange={(e) => pickCv(e.target.files?.[0])}
                                                className="sr-only"
                                                data-testid="careers-cv-input"
                                            />
                                        </label>
                                    )}
                                    <p className="mt-2 text-xs text-[#1C1C1C]/50">{t.careers.f.cvHint}</p>
                                </Field>

                                <div className="flex items-center justify-between gap-6">
                                    <p className="text-xs text-[#1C1C1C]/55 max-w-md leading-relaxed">
                                        {t.careers.consent}
                                    </p>
                                    <button
                                        type="submit"
                                        disabled={busy}
                                        className="inline-flex items-center gap-2 px-7 h-12 bg-[#1C1C1C] text-[#F9F9F7] text-[12px] uppercase tracking-[0.22em] font-bold hover:bg-[#9B7A58] disabled:opacity-50 transition-colors"
                                        data-testid="careers-submit"
                                    >
                                        {busy ? (
                                            <>
                                                <Loader2 size={14} className="animate-spin" />
                                                {t.careers.sending}
                                            </>
                                        ) : (
                                            t.careers.send
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </section>

            <style>{`
                .cf-input {
                    width: 100%;
                    background: transparent;
                    border: 0;
                    border-bottom: 1.5px solid rgba(28,28,28,0.18);
                    padding: 12px 0;
                    font-size: 16px;
                    color: #1C1C1C;
                    outline: none;
                    transition: border-color .25s ease;
                    font-family: inherit;
                }
                .cf-input:focus { border-bottom-color: #1C1C1C; }
                .cf-input::placeholder { color: rgba(28,28,28,0.35); }
            `}</style>
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: "#9B7A58" }}>
                {label}
                {required && <span className="ml-1 text-[#1C1C1C]/40">*</span>}
            </span>
            <div className="mt-2">{children}</div>
        </label>
    );
}
